import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { v1Get, v1Patch, v1Post, v1Delete, v1Upload } from '@/lib/api';
import { displayStageLabel, IQ_ASSESSMENT_LABEL } from '@/lib/stageLabels';
import { buildStageStatuses } from '@/lib/pipelineStageStatus';
import { usePipelineAssessmentReadiness } from '@/hooks/usePipelineAssessmentReadiness';
import { useHasPermission } from '@/auth/permissions';
import { isEvalUiStale } from '@/lib/evalErrors';
import { resolveEvaluationSelection } from '@/lib/candidateDetailTab';
import { toast } from 'sonner';
import { toastEmailDispatch } from '@/lib/emailDispatchToast';

export const STAGE_LABEL = {
  RESUME: 'Resume',
  VIDEOBOT: 'Video Bot',
  MCQ: IQ_ASSESSMENT_LABEL,
  TECH_ASSESSMENT: 'Tech Assessment',
  PANEL: 'Panel Interview',
  AI_REPORT: 'AI Report',
  CLIENT_SHARE: 'Client Share',
  OFFER: 'Offer',
};

function isEvalStillRunning(status, startedAt) {
  if (status === 'pending') return true;
  if (status !== 'in_progress') return false;
  // Stop polling only when in_progress is past the reclaim window (dead worker).
  return !isEvalUiStale(startedAt);
}

export function useCandidateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();
  const [selectedEvaluationId, setSelectedEvaluationId] = useState(null);
  const { data: evaluations = [], isLoading: evaluationsLoading, refetch: refetchEvaluations } = useQuery({
    queryKey: ['v1-candidate-evaluations', id],
    queryFn: () => v1Get(`/candidates/${id}/evaluations`),
    staleTime: 10_000,
  });

  // Honour ?eval= (or legacy ?evaluation_id=) from deeplinks; otherwise default
  // to the first evaluation so stage APIs always receive evaluation_id.
  // Keep eval in the URL (do not strip) so Strict Mode remount cannot fall back
  // to newest-first while selectedEvaluationId is briefly null.
  const evalParam = searchParams.get('eval') || searchParams.get('evaluation_id');
  const panelParam = searchParams.get('panel');

  // Legacy panel notifs may only carry ?panel= — resolve owning evaluation_id.
  const panelDeeplinkNeedsEval = Boolean(panelParam && !evalParam);
  const { data: panelDeeplinkMeta, isFetched: panelDeeplinkFetched } = useQuery({
    queryKey: ['v1-panel-deeplink-meta', panelParam],
    queryFn: () => v1Get(`/panels/${panelParam}`),
    enabled: panelDeeplinkNeedsEval,
    staleTime: 60_000,
    retry: 1,
  });
  const panelEvaluationId = panelDeeplinkMeta?.evaluation_id || null;
  const panelDeeplinkPending = panelDeeplinkNeedsEval && !panelDeeplinkFetched;

  const evaluationIds = evaluations.map((e) => e.id);
  const resolvedEval = resolveEvaluationSelection({
    evalFromUrl: evalParam,
    evaluationIds,
    panelEvaluationId,
    selectedEvaluationId,
    panelDeeplinkPending,
  });
  const effectiveEvaluationId = resolvedEval.evaluationId;

  const { data: c } = useQuery({
    // Per-evaluation data scoping. Backend requires evaluation_id on GET.
    queryKey: ['v1-candidate', id, effectiveEvaluationId],
    queryFn: () => v1Get(`/candidates/${id}?evaluation_id=${effectiveEvaluationId}`),
    enabled: !!id && !!effectiveEvaluationId,
    // Auto-poll only while stage AI is mid-flight. Do not poll just because a
    // panel meeting is scheduled/syncable — Sync is manual / on-demand.
    refetchInterval: (query) => {
      const data = query?.state?.data ?? query;
      const vbResponses = data?.videobot?.responses || [];
      const vbPending = vbResponses.some(
        (r) => isEvalStillRunning(r.eval_status, r.eval_started_at),
      );
      const mcqEval = data?.mcq?.eval;
      const mcqPending = isEvalStillRunning(mcqEval?.eval_status, mcqEval?.eval_started_at);
      const techEval = data?.tech_assessment?.eval;
      const techPending = isEvalStillRunning(techEval?.eval_status, techEval?.eval_started_at);
      const panels = data?.panels || [];
      const panelPending = panels.some(
        (p) => isEvalStillRunning(p?.eval?.eval_status, p?.eval?.eval_started_at),
      );
      return (vbPending || mcqPending || techPending || panelPending) ? 4000 : false;
    },
    // Preserve presigned S3 URLs across polling cycles so HTML5 video elements
    // do not repeatedly abort, flicker, and restart playback.
    structuralSharing: (oldData, newData) => {
      if (!oldData || !newData) return newData;
      let changed = false;

      // 1. Videobot responses
      const oldVbResponses = oldData?.videobot?.responses || [];
      const newVbResponses = newData?.videobot?.responses || [];
      let mergedVbResponses = newVbResponses;
      if (oldVbResponses.length && newVbResponses.length) {
        const urlByResponseId = Object.fromEntries(
          oldVbResponses
            .filter((r) => r.id && r.video_url)
            .map((r) => [r.id, r.video_url]),
        );
        let vbChanged = false;
        mergedVbResponses = newVbResponses.map((r) => {
          const preserved = urlByResponseId[r.id];
          if (preserved && preserved !== r.video_url) {
            vbChanged = true;
            return { ...r, video_url: preserved };
          }
          return r;
        });
        if (vbChanged) changed = true;
      }

      // 2. MCQ session recording
      let mcqMerged = newData?.mcq;
      const oldMcqUrl = oldData?.mcq?.recording?.video_url;
      const newMcqUrl = newData?.mcq?.recording?.video_url;
      if (oldMcqUrl && newMcqUrl && oldMcqUrl !== newMcqUrl) {
        changed = true;
        mcqMerged = {
          ...newData.mcq,
          recording: {
            ...newData.mcq.recording,
            video_url: oldMcqUrl,
          },
        };
      }

      // 3. Tech assessment recording
      let techMerged = newData?.tech_assessment;
      const oldTechUrl = oldData?.tech_assessment?.recording?.video_url;
      const newTechUrl = newData?.tech_assessment?.recording?.video_url;
      if (oldTechUrl && newTechUrl && oldTechUrl !== newTechUrl) {
        changed = true;
        techMerged = {
          ...newData.tech_assessment,
          recording: {
            ...newData.tech_assessment.recording,
            video_url: oldTechUrl,
          },
        };
      }

      // 4. Candidate photo URL
      let photoMerged = newData?.photo_url;
      if (oldData?.photo_url && newData?.photo_url && oldData.photo_url !== newData.photo_url) {
        changed = true;
        photoMerged = oldData.photo_url;
      }

      if (!changed) return newData;

      return {
        ...newData,
        photo_url: photoMerged,
        mcq: mcqMerged,
        tech_assessment: techMerged,
        videobot: newData?.videobot ? {
          ...newData.videobot,
          responses: mergedVbResponses,
        } : newData?.videobot,
      };
    },
  });

  // Activity tab: This evaluation (default) vs All evaluations
  const [activityScope, setActivityScope] = useState('evaluation');
  const {
    data: activityPage,
    isLoading: activityLoading,
    isError: activityError,
  } = useQuery({
    queryKey: ['v1-candidate-activity', id, effectiveEvaluationId, activityScope],
    queryFn: () => {
      if (activityScope === 'all') {
        return v1Get(`/candidates/${id}/activity?scope=all&page_size=50`);
      }
      return v1Get(
        `/candidates/${id}/activity?scope=evaluation&evaluation_id=${encodeURIComponent(effectiveEvaluationId)}&page_size=50`,
      );
    },
    enabled: !!id && !!effectiveEvaluationId,
  });
  const activityItems = activityPage?.items || [];

  const invalidateCandidateViews = async () => {
    await qc.invalidateQueries({ queryKey: ['v1-candidate', id] });
    await qc.invalidateQueries({ queryKey: ['v1-candidate-activity', id] });
  };
  const assessmentReadiness = usePipelineAssessmentReadiness(c?.pipeline_id);

  const [genLinkOpen, setGenLinkOpen] = useState(false);
  const [genLinkStage, setGenLinkStage] = useState('VIDEOBOT');
  const [genLinkExpiry, setGenLinkExpiry] = useState(72);
  // Warning when a combined invite was already sent for this evaluation.
  const [combinedWarnOpen, setCombinedWarnOpen] = useState(false);
  const [combinedWarnAction, setCombinedWarnAction] = useState(null); // { type: 'send'|'reissue', stage?, linkId? }

  // Sprint 11.5 — Lifecycle status change popover.
  // Bidirectional moves between in_progress / on_hold / rejected are allowed
  // and require a non-empty reason that gets written to the audit log.
  const [statusPopoverOpen, setStatusPopoverOpen] = useState(false);
  const [statusDraft, setStatusDraft] = useState('in_progress');
  const [statusReason, setStatusReason] = useState('');
  const [statusSaving, setStatusSaving] = useState(false);

  // Delete candidate (soft-delete) — admin / HR Manager only via candidate.delete.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Sprint 11.5: Edit Profile state + dialog removed. Profile editing now lives
  // exclusively in the Resume Enrichment workspace (/portal/resume-enrichment/:id).
  //
  // Sprint 14.1b — Real Candidate Evaluations replace the legacy "Re-apply"
  // toast. ``newEvalOpen`` controls the New Evaluation modal; ``closeEvalOpen``
  // controls the close confirmation; ``selectedEvaluationId`` (declared
  // above) drives the active pill (mirrored into ``activeRecordId`` for
  // backward compat with the existing record-merging logic).
  const [newEvalOpen, setNewEvalOpen] = useState(false);
  const [closeEvalOpen, setCloseEvalOpen] = useState(false);
  const [activeRecordId, setActiveRecordId] = useState(null);

  // Sprint 14.1b — Honour the ``?action=new-eval`` and ``?eval=...`` query
  // params from the duplicate-email modal in CandidateNew. Runs once when
  // the evaluations list resolves. Also default the pill to the first eval
  // so stage hydrators always receive evaluation_id (no cross-eval mix).
  // Keep ``?eval=`` and ``?tab=`` so notification deeplinks survive remount.
  React.useEffect(() => {
    const action = searchParams.get('action');
    const evalFromUrl = searchParams.get('eval') || searchParams.get('evaluation_id');
    if (action === 'new-eval') {
      setNewEvalOpen(true);
      // Clear the query string so a refresh doesn't reopen the modal.
      const next = new URLSearchParams(searchParams);
      next.delete('action');
      setSearchParams(next, { replace: true });
      return;
    }

    const pick = resolveEvaluationSelection({
      evalFromUrl,
      evaluationIds: evaluations.map((e) => e.id),
      panelEvaluationId,
      selectedEvaluationId,
      panelDeeplinkPending,
    });

    if (pick.source === 'pending' || !pick.evaluationId) return;

    if (pick.evaluationId !== selectedEvaluationId) {
      setSelectedEvaluationId(pick.evaluationId);
      setActiveRecordId(pick.evaluationId);
    }

    // Normalize legacy evaluation_id → eval; write eval when resolved from panel.
    // Never strip eval — Strict Mode remount needs it.
    const needsEvalWrite = !searchParams.get('eval')
      && (searchParams.get('evaluation_id') || pick.source === 'panel');
    if (needsEvalWrite && pick.evaluationId) {
      const next = new URLSearchParams(searchParams);
      next.set('eval', pick.evaluationId);
      next.delete('evaluation_id');
      setSearchParams(next, { replace: true });
    }
  }, [
    evaluations,
    searchParams,
    setSearchParams,
    selectedEvaluationId,
    panelEvaluationId,
    panelDeeplinkPending,
  ]);

  // Sprint 15.2 — derived read-only signal. The selected evaluation (defaulting
  // to the first/most-recent) drives whether stage-mutating actions are
  // disabled. Cascaded onto stage tabs below via <fieldset disabled>.
  const selectedEvaluation = evaluations.find(
    (e) => e.id === effectiveEvaluationId,
  );
  const isReadOnlyForActions = selectedEvaluation?.status === 'closed';
  const { data: resumeTemplate } = useQuery({ queryKey: ['v1-resume-template'], queryFn: () => v1Get('/resume-templates/active'), staleTime: 60_000 });
  const { data: companyResume, refetch: refetchCompanyResume } = useQuery({
    queryKey: ['v1-company-resume', id, effectiveEvaluationId],
    queryFn: () => v1Get(`/candidates/${id}/company-resume?evaluation_id=${effectiveEvaluationId}`),
    enabled: !!id && !!effectiveEvaluationId,
    staleTime: 30_000,
  });
  const { data: v1Links, refetch: refetchLinks } = useQuery({
    queryKey: ['v1-candidate-links', id, effectiveEvaluationId],
    queryFn: async () => {
      try {
        return await v1Get(`/candidates/${id}/links?evaluation_id=${effectiveEvaluationId}`);
      } catch {
        return [];
      }
    },
    enabled: !!id && !!effectiveEvaluationId,
    staleTime: 15_000,
  });
  const { data: combinedInviteStatus, refetch: refetchCombinedInviteStatus } = useQuery({
    queryKey: ['v1-combined-invite-status', id, effectiveEvaluationId],
    queryFn: async () => {
      try {
        return await v1Get(
          `/candidates/${id}/combined-assessment-invite/status?evaluation_id=${effectiveEvaluationId}`,
        );
      } catch {
        return { sent: false };
      }
    },
    enabled: !!id && !!effectiveEvaluationId,
    staleTime: 15_000,
  });
  const combinedInviteAlreadySent = !!combinedInviteStatus?.sent;
  const [resumeViewOpen, setResumeViewOpen] = useState(false);
  const [generating, setGenerating] = React.useState(false);
  const [downloadingResume, setDownloadingResume] = useState(false);
  // Sprint 11.5 cleanup (B) — Upload original resume directly from the detail
  // page when none is on file. Hidden <input> is triggered programmatically.
  const fileInputRef = React.useRef(null);
  const [uploadingResume, setUploadingResume] = useState(false);
  // Replace/upload original resume is evaluation stage work (evaluation.edit).
  const canReplaceResume = useHasPermission('evaluation.edit');

  // Sprint 11.5 cleanup (C) — Inline editor for the Assigned HR owner.
  const [hrEditOpen, setHrEditOpen] = useState(false);
  const [hrSaving, setHrSaving] = useState(false);

  const saveAssignedHr = async (newHrId) => {
    if (!newHrId || newHrId === c.assigned_hr_id) {
      setHrEditOpen(false);
      return;
    }
    setHrSaving(true);
    try {
      await v1Patch(`/candidates/${id}`, { assigned_hr_id: newHrId });
      await invalidateCandidateViews();
      setHrEditOpen(false);
      toast.success('Assigned HR updated.');
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Could not update assigned HR';
      toast.error(typeof detail === 'string' ? detail : 'Could not update assigned HR');
    } finally {
      setHrSaving(false);
    }
  };

  // The single canonical original resume file (kind=resume) for this candidate.
  const resumeFile = React.useMemo(
    () => (c?.files || []).find((f) => f.kind === 'resume' && f.status === 'uploaded') || null,
    [c?.files],
  );

  const onPickResumeFile = () => {
    if (uploadingResume || !canReplaceResume) return;
    fileInputRef.current?.click();
  };

  const onResumeFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!effectiveEvaluationId) {
      toast.error('Select an evaluation before uploading a resume.');
      return;
    }
    const ok = /\.(pdf|docx)$/i.test(file.name);
    if (!ok) {
      toast.error('Only PDF or DOCX resumes are supported.');
      return;
    }
    setUploadingResume(true);
    try {
      // Backend-proxied upload: the browser cannot PUT directly to the
      // presigned S3 URL (bucket CORS policy pending), so the backend streams
      // the file into S3 and finalizes in a single multipart call.
      await v1Upload(`/candidates/${id}/files/upload`, file, {
        kind: 'resume',
        evaluation_id: effectiveEvaluationId,
      });
      await invalidateCandidateViews();
      toast.success('Resume uploaded. You can now generate the company resume.');
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Upload failed';
      toast.error(typeof detail === 'string' ? detail : 'Upload failed');
    } finally {
      setUploadingResume(false);
    }
  };

  const downloadOriginalResume = async () => {
    if (!resumeFile || downloadingResume) return;
    setDownloadingResume(true);
    try {
      const { url } = await v1Get(`/candidates/${id}/files/${resumeFile.id}/download`);
      // Open in a new tab — S3 returns the file as an attachment thanks to
      // the response-content-disposition header set by the backend.
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Could not fetch download URL.';
      toast.error(typeof detail === 'string' ? detail : 'Download failed');
    } finally {
      setDownloadingResume(false);
    }
  };

  const generateResume = async () => {
    if (generating || !effectiveEvaluationId) return;
    setGenerating(true);
    try {
      await v1Post(
        `/candidates/${id}/company-resume/generate?evaluation_id=${encodeURIComponent(effectiveEvaluationId)}`,
        {},
      );
      await refetchCompanyResume();
      toast.success('Company resume generated.');
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Generation failed';
      toast.error(typeof detail === 'string' ? detail : 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  // Resolve active interview record (latest by default)
  const records = c ? (c.interview_records && c.interview_records.length ? c.interview_records : [{
    id: 'rec-default', label: 'Round 1', applied_role: c.role, applied_role_label: c.role_label,
    department: c.department, department_label: c.department_label,
    sub_department: c.sub_department || '', sub_department_label: c.sub_department_label || '',
    pipeline_key: c.pipeline_key, created_at: c.created_at, updated_at: c.updated_at,
    status: c.status, current_stage: c.current_stage,
    links: c.links || [], videobot: c.videobot, mcq: c.mcq, tech_assessment: c.tech_assessment,
    panels: c.panels || [],
    ai_reports: c.ai_reports || [], client_links: c.client_links || [], is_latest: true,
  }]) : [];
  const activeRecord = records.length
    ? (records.find((r) => r.id === activeRecordId) || records.find((r) => r.is_latest) || records[0])
    : null;
  // Build a "view" with the active record's data merged onto candidate (for tab content compatibility)
  const view = c && activeRecord ? {
    ...c,
    current_stage: activeRecord.current_stage,
    links: activeRecord.links || [],
    videobot: activeRecord.videobot,
    mcq: activeRecord.mcq,
    tech_assessment: activeRecord.tech_assessment ?? c.tech_assessment,
    panels: activeRecord.panels || [],
    offer: c.offer || null,
    ai_reports: activeRecord.ai_reports || [],
    client_links: activeRecord.client_links || [],
    pipeline_key: activeRecord.pipeline_key,
  } : null;

  const stageDetails = Array.isArray(c?.pipeline_stage_details) ? c.pipeline_stage_details : [];
  // Evidence-based completion / pending — ignore stale view.current_stage.
  const stageStatuses = c && view ? buildStageStatuses(
    stageDetails.length
      ? stageDetails
      : (c.pipeline_stages || []).map((code, i) => ({ stage_code: code, label: displayStageLabel(code), position: i + 1 })),
    {
      hasResumeFile: Boolean(resumeFile),
      hasCompanyResume: Boolean(companyResume),
      videobot: view.videobot,
      mcq: view.mcq,
      tech_assessment: view.tech_assessment,
      panels: view.panels || [],
      offer: view.offer,
      links: Array.isArray(v1Links) ? v1Links : (view.links || []),
    },
  ) : [];

  // Sprint 11.6 (#9) — Dynamic tabs derived from the candidate's pipeline.
  // Only show Video Bot / MCQ / Panel tabs if the candidate's pinned
  // pipeline version actually includes those stages. Client Report + Activity
  // are always shown (artefacts/audit, not pipeline stages). Tab *order*
  // must follow pinned pipeline_stage_details.position — same SSoT as
  // PipelineTimeline — not a hardcoded VB→MCQ→Panel sequence.
  const pipelineStageSet = new Set(
    (c?.pipeline_stages || []).map((code) => String(code || '').toLowerCase()),
  );
  const panelRounds = stageDetails.filter((s) => {
    const code = (s.stage_code || '').toLowerCase();
    return code === 'panel' || code === 'code_panel';
  });
  // Ordered tab descriptors for TabsList (pipeline order). TabsContent keys
  // stay stable by value; only trigger order changes with the pin.
  let panelTabIdx = 0;
  const orderedStageTabs = (stageDetails.length
    ? stageDetails
    : (c?.pipeline_stages || []).map((code, i) => ({
      stage_code: code,
      label: displayStageLabel(code),
      position: i + 1,
    }))
  ).map((s) => {
    const code = String(s.stage_code || s.code || '').toLowerCase();
    if (code === 'resume') {
      return {
        type: 'resume',
        value: 'resume',
        label: displayStageLabel('RESUME', s.label),
        testId: 'tab-resume',
      };
    }
    if (code === 'videobot') {
      return {
        type: 'videobot',
        value: 'videobot',
        label: displayStageLabel('VIDEOBOT', s.label),
        testId: 'tab-videobot',
      };
    }
    if (code === 'mcq') {
      return {
        type: 'mcq',
        value: 'mcq',
        label: displayStageLabel('MCQ', s.label),
        testId: 'tab-mcq',
      };
    }
    if (code === 'tech_assessment') {
      return {
        type: 'tech_assessment',
        value: 'tech-assessment',
        label: displayStageLabel('TECH_ASSESSMENT', s.label),
        testId: 'tab-tech-assessment',
      };
    }
    if (code === 'panel' || code === 'code_panel') {
      const i = panelTabIdx;
      panelTabIdx += 1;
      return {
        type: 'panel',
        value: `panel-${s.id || i}`,
        label: s.label || `Panel Interview ${i + 1}`,
        testId: `tab-panel-${i}`,
      };
    }
    if (code === 'offer') {
      return {
        type: 'offer',
        value: 'offer',
        label: displayStageLabel('OFFER', s.label),
        testId: 'tab-offer',
      };
    }
    return null;
  }).filter(Boolean);

  const refresh = () => {
    void invalidateCandidateViews();
  };
  const generateLink = async () => {
    if (!effectiveEvaluationId) {
      toast.error('Select an evaluation before generating a link.');
      return;
    }
    try {
      const link = await v1Post(`/candidates/${id}/links`, {
        stage_code: genLinkStage,
        expires_in_hours: genLinkExpiry,
        evaluation_id: effectiveEvaluationId,
      });
      setGenLinkOpen(false);
      refresh();
      refetchLinks();
      toast.success(`${STAGE_LABEL[genLinkStage]} link generated.`);
      toastEmailDispatch(link?.email_dispatch);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not generate link');
    }
  };

  const requestSendStageLink = (stageCode) => {
    setGenLinkStage(stageCode);
    if (combinedInviteAlreadySent) {
      setCombinedWarnAction({ type: 'send', stage: stageCode });
      setCombinedWarnOpen(true);
      return;
    }
    setGenLinkOpen(true);
  };

  const revokeLink = async (linkId) => {
    try {
      await v1Post(`/candidates/${id}/links/${linkId}/revoke`, {});
      refetchLinks();
      await invalidateCandidateViews();
      toast.success('Link revoked');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not revoke link');
    }
  };

  const reissueLink = async (linkId) => {
    try {
      const link = await v1Post(`/candidates/${id}/links/${linkId}/reissue`, {});
      refetchLinks();
      await invalidateCandidateViews();
      toast.success('New invitation issued');
      toastEmailDispatch(link?.email_dispatch);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not reissue link');
    }
  };

  const requestReissueLink = (linkId) => {
    if (combinedInviteAlreadySent) {
      setCombinedWarnAction({ type: 'reissue', linkId });
      setCombinedWarnOpen(true);
      return;
    }
    void reissueLink(linkId);
  };

  const confirmCombinedWarn = () => {
    const action = combinedWarnAction;
    setCombinedWarnOpen(false);
    setCombinedWarnAction(null);
    if (!action) return;
    if (action.type === 'send') {
      if (action.stage) setGenLinkStage(action.stage);
      setGenLinkOpen(true);
      return;
    }
    if (action.type === 'reissue' && action.linkId) {
      void reissueLink(action.linkId);
    }
  };

  // Sprint 11.6 (#8) — copy a candidate link URL to the clipboard and stamp
  // delivery_method="manual_copy" on the server so the audit trail reflects
  // that HR delivered it out-of-band (e.g., via WhatsApp).
  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link.candidate_url);
      toast.success('Link copied to clipboard');
      // Best-effort tracking — never block the copy on the audit POST.
      try {
        await v1Post(`/candidates/${id}/links/${link.id}/track-delivery`, { method: 'manual_copy' });
        refetchLinks();
      } catch { /* swallow */ }
    } catch {
      toast.error('Clipboard not available in this browser.');
    }
  };

  // Sprint 12 — stageAction() removed. The legacy `/api/candidates/{id}/stage-action`
  // endpoint mutated in-memory STORE only (silent data loss) and was never wired
  // into the UI. Pipeline-stage advancement is auto-driven by submission events;
  // hold/reject is handled by the status popover which calls
  // POST /api/v1/candidates/{id}/status (real DB write + audit row).

  const openStatusPopover = () => {
    setStatusDraft(c.status || 'in_progress');
    setStatusReason('');
    setStatusPopoverOpen(true);
  };

  const submitStatusChange = async () => {
    const trimmed = statusReason.trim();
    if (trimmed.length < 3) {
      toast.error('Please provide a reason (min 3 characters).');
      return;
    }
    if (statusDraft === c.status) {
      toast.info('Status is unchanged.');
      setStatusPopoverOpen(false);
      return;
    }
    setStatusSaving(true);
    try {
      await v1Post(`/candidates/${id}/status`, { status: statusDraft, reason: trimmed });
      await invalidateCandidateViews();
      setStatusPopoverOpen(false);
      const label = statusDraft === 'on_hold' ? 'On hold' : statusDraft === 'rejected' ? 'Rejected' : 'In Progress';
      toast.success(`Candidate status updated to ${label}.`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Could not update status';
      toast.error(typeof detail === 'string' ? detail : 'Could not update status');
    } finally {
      setStatusSaving(false);
    }
  };

  const submitDelete = async () => {
    setDeleting(true);
    try {
      await v1Delete(`/candidates/${id}`);
      await qc.invalidateQueries({ queryKey: ['v1-candidates'] });
      setDeleteOpen(false);
      toast.success('Candidate deleted.');
      navigate('/portal/candidates');
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Could not delete candidate';
      toast.error(typeof detail === 'string' ? detail : 'Could not delete candidate');
      setDeleting(false);
    }
  };

  return {
    id,
    navigate,
    c,
    evaluations,
    evaluationsLoading,
    effectiveEvaluationId,
    selectedEvaluationId,
    setSelectedEvaluationId,
    activeRecordId,
    setActiveRecordId,
    isReadOnlyForActions,
    selectedEvaluation,
    view,
    stageStatuses,
    pipelineStageSet,
    panelRounds,
    orderedStageTabs,
    resumeFile,
    companyResume,
    resumeTemplate,
    v1Links,
    combinedInviteAlreadySent,
    assessmentReadiness,
    activityScope,
    setActivityScope,
    activityItems,
    activityLoading,
    activityError,
    invalidateCandidateViews,
    refetchEvaluations,
    refetchLinks,
    refetchCombinedInviteStatus,
    genLinkOpen,
    setGenLinkOpen,
    genLinkStage,
    setGenLinkStage,
    genLinkExpiry,
    setGenLinkExpiry,
    combinedWarnOpen,
    setCombinedWarnOpen,
    combinedWarnAction,
    setCombinedWarnAction,
    statusPopoverOpen,
    setStatusPopoverOpen,
    statusDraft,
    setStatusDraft,
    statusReason,
    setStatusReason,
    statusSaving,
    deleteOpen,
    setDeleteOpen,
    deleting,
    newEvalOpen,
    setNewEvalOpen,
    closeEvalOpen,
    setCloseEvalOpen,
    resumeViewOpen,
    setResumeViewOpen,
    generating,
    downloadingResume,
    uploadingResume,
    fileInputRef,
    canReplaceResume,
    hrEditOpen,
    setHrEditOpen,
    hrSaving,
    saveAssignedHr,
    onPickResumeFile,
    onResumeFileSelected,
    downloadOriginalResume,
    generateResume,
    generateLink,
    requestSendStageLink,
    revokeLink,
    requestReissueLink,
    confirmCombinedWarn,
    copyLink,
    openStatusPopover,
    submitStatusChange,
    submitDelete,
  };
}
